import {MainHelper} from "../../shared/models/main-helper";
import {ColumnSettingsBase} from "./column-settings-base";
import {ISlickGridColumn} from "../interfaces/slick-grid-column";
import {Aggregation, AggregationSelector} from "../../stocks/models/aggregation-selector";
import {Formatters} from "./formatters";
import {TranslateService} from "@ngx-translate/core";
import * as moment from 'moment';
import {ElementRef, Renderer2} from "@angular/core";
import {NumericEditor} from "./numeric-editor";
import {SelectEditor} from "./select-editor";
import {TextEditor} from "./text-editor";
import {Validators} from "./validators";
import {CommonDataService} from "../../services/common-data.service";
import {TimeEditor} from "./time-editor";
import {TimeValidationParams} from "./time-validation-params";

export class ColumnsGenerator<T> {
    constructor( private readonly translateService: TranslateService,
                 private readonly renderer: Renderer2,
                 private readonly elementRef: ElementRef
    ) {

        this.helper = new MainHelper();
        this.validators = new Validators(this.translateService);
        this.toLower = true;
        this.minWidth = 0;
    }

    private helper: MainHelper;

    private validators: Validators;

    public toLower: boolean;

    public minWidth: number;

    public addCustomFormatter: (columnSettings: ColumnSettingsBase[], columns: ISlickGridColumn<T>[]) => void;

    public addSourceFilter: (columnSettings: ColumnSettingsBase[], columns: ISlickGridColumn<T>[]) => void;

    public addTotalsFormatter: (columnSettings: ColumnSettingsBase[], columns: ISlickGridColumn<T>[]) => void;

    public getOuterValidationFunc: (functionName: string) => Slick.ValidateResults;

    private getFormatter = (formatter: string, translates: {[key: string]: string}): any => {
        const setFormatter = (formatter: string): any => {
            return formatter === 'nAuto'
                ? (r, c, v) => Formatters.numberFormatterAuto(r, c, v)
                : (r, c, v) => Formatters.numberFormatter(r, c, v, this.getDecimalPlaces(formatter));
        }

        switch (formatter){
            case 'percent':
                return Formatters.percentFormatter;
            case 'date':
                return Formatters.dateFormatterConv;
            case 'multidate':
                return Formatters.multiDateFormatterConv;
            case 'dateActionTab':
                return Formatters.dateFormatterConvActionTab;
            case 'time':
                return Formatters.timeFormatter;
            case 'datetime':
                return Formatters.dateTimeFormatterConv;
            case 'nAuto':
                return setFormatter(formatter);
            case 'n0':
                return setFormatter(formatter);
            case 'n1':
                return setFormatter(formatter);
            case 'n2':
                return setFormatter(formatter);
            case 'n3':
                return setFormatter(formatter);
            case 'n4':
                return setFormatter(formatter);
            case 'boolean':
                return (r, c, v) => Formatters.booleanFormatter(r, c, v);
            case 'yesNo':
                return (r, c, v) => Formatters.yesNo(r, c, v, translates);
            case 'yesNoInt':
                return (r, c, v) => Formatters.yesNoInt(r, c, v, translates);
            case 'eye-cell':
                return (r, c, v) => Formatters.icons.eyeCell(r, c, v, translates);
            case 'registry-fresh':
                return (r, c, v) => Formatters.registryBT(r, c, v, translates);
            case 'ioStatusLine':
                return (r, c, v) => Formatters.IOHLineFormatter(r, c, v, translates);
            case 'vc-cancel':
                return (r, c, v) => Formatters.vcCancel(r, c, v, translates);
            case 'activity':
                return (r, c, v) => Formatters.Activity(r, c, v);
            case 'packedYesNo':
                return (r, c, v) => Formatters.packedYesNo(r, c, v, translates);
            case 'newMover':
                return (r, c, v) => Formatters.newMoverYes(r, c, v, translates);
            default: return null;
        }
    }

    private readonly getDecimalPlaces = (formatter: string): number => {
        switch (formatter){
            case 'n0':
                return 0;
            case 'n1':
                return 1;
            case 'n2':
                return 2;
            case 'n3':
                return 3;
            case 'n4':
                return 4;
        }
    }

    public numericEditor = (editor, getValidator: (val: string) => any): any => {
        return (a) => new NumericEditor(this.renderer, a, editor.decimalPlaces, getValidator(editor.validator), editor.allowNull)
    }

    //# generate columns for grids in tab
    public async columnsGenerate(columns: ISlickGridColumn<T>[], columnsSettings: ColumnSettingsBase[], isAdmin, test: boolean = false): Promise<ISlickGridColumn<T>[]>{
        if (test) {console.log(columnsSettings, 'columnsGenerate');}
        let toTranslate = columnsSettings.map(item => item.translateKey)
                            .concat(columnsSettings.map(item => item.descriptionKey))
                            .concat(columnsSettings.map(item => item.group))
                            .concat('Yes', 'No', 'Fresh2', 'NonFresh', 'Show', 'Transit', 'Cancel', 'Arrived', 'Canceled','PackYes','PackNo','NewMover' );

        toTranslate = toTranslate.reduce((unique: string[], item: string) => unique.includes(item) ? unique : [...unique, item], []).filter( t => t);
        let translates = await this.translateService.get(toTranslate).toPromise();

        columnsSettings.sort((a: ColumnSettingsBase, b: ColumnSettingsBase) => {
            return a.position - b.position;
        });

        columnsSettings.forEach(item =>{
            // let column: Slick.Column<any> = {id: prop, field: prop, name: this.settings[prop].translateKey, sortable: true, width: 100, groupTotalsFormatter: this.totalsFormatter};
            let regColId = item.columnName.replace(/[\s+]|[,+]|[.+]|[(+]|[)+]|["+]|['+]/g, '_');
            let column: Slick.Column<T> = {
                id: this.toLower ? this.helper.lowerCaseFirstLetter(item.columnName) : item.columnName,
                field: this.toLower ? this.helper.lowerCaseFirstLetter(item.columnName) : item.columnName,
                name: translates[item.translateKey],
                sortable: true,
                width: item.width,
                grouping:
                item.grouping
                    ? { grouped: false,
                        aggregated: false,
                        aggregation: null,
                        dropdownContainer: this.elementRef.nativeElement,
                        renderer: this.renderer}
                    : null,
                type: item.type,
                excelCellBlock: item.excelCellBlock,
                description: item.descriptionVisibility === 1 ? translates[item.descriptionKey] : '',
                descriptionKey: item.descriptionKey,
                regColId: regColId,
                disabled: item.disabled,
                cssClass: ''
            };

            //# add possibility apply aggregation to column from settings
            if (item.type === 'number' && item.grouping && item.aggregation){
                column.allowAggregation = item.aggregation;
                column.grouping.aggregation = new AggregationSelector(item.columnName, translates[item.translateKey], new Aggregation(false, false, false, false));
                column.cssClass = 'text-right';
            }

            //# add possibility add current filter type to column from settings
            if (item.filterType){
                switch (item.filterType){
                    case 'dropdowndate':
                        column.dropdownFilter = {
                            type: 'reverse',
                            filtered: false,
                            dropdownContainer: this.elementRef.nativeElement,
                            renderer: this.renderer,
                            displayFormatter: (r, c, v) => Formatters.dateFormatterConv(null, null, v),
                            sortFormatter: v => v == null ? '' : moment.utc(v).format(MainHelper.dateTimeFormat)
                        };
                        break;
                    case 'dropdowndatetime':
                        column.dropdownFilter = {
                            type: 'reverse',
                            filtered: false,
                            dropdownContainer: this.elementRef.nativeElement,
                            renderer: this.renderer,
                            displayFormatter: (r, c, v) => Formatters.dateTimeFormatterConv(null, null, v),
                            sortFormatter: v => v == null ? '' : moment.utc(v).format(MainHelper.dateTimeFormat)
                        };
                        break;
                    //case 'dropdownboolean': column.dropdownFilter = { filtered: false, dropdownContainer: this.elementRef.nativeElement, renderer: this.renderer, displayFormatter: v => Formatters.booleanFormatter(null, null, v)}; break;
                    case 'dropdown':
                        column.dropdownFilter = {
                            filtered: false,
                            dropdownContainer: this.elementRef.nativeElement,
                            renderer: this.renderer,
                            displayFormatter: item.formatter ? this.getFormatter(item.formatter, translates) : null
                        };
                        break;
                    case 'search-string':
                        column.inputFilter = {
                            filtered: false,
                            dropdownContainer: this.elementRef.nativeElement,
                            renderer: this.renderer,
                            type: 'string',
                            displayFormatter: item.formatter ? this.getFormatter(item.formatter, translates) : null
                        };
                        break;
                    case 'search-string-to-number':
                        column.inputFilter = {
                            filtered: false,
                            dropdownContainer: this.elementRef.nativeElement,
                            renderer: this.renderer,
                            type: 'string',
                            toNumber: true,
                            displayFormatter: item.formatter ? this.getFormatter(item.formatter, translates) : null
                        };
                        break;
                    case 'search-number':
                        column.inputFilter = {
                            filtered: false,
                            dropdownContainer: this.elementRef.nativeElement,
                            renderer: this.renderer,
                            type: 'number'
                        };
                        break;
                }
            }

            //# add formatter from settings to column
            if (item.formatter){
                column.formatterStr = item.formatter;
                column.formatter = this.getFormatter(item.formatter, translates)
            }

            if (item.group){
                column.group = translates[item.group];
            }

            //# return validator
            const getValidator = (name: string): any => {
                switch (name) {
                    case 'numeric_allowzero': return this.validators.allowZeroNumberValidator;
                    case 'numeric_notallowzero': return this.validators.notAllowZeroNumberValidator;
                    case 'numeric_notallowzero_time': return this.validators.notAllowZeroNumberValidatorMax24AllowNull;
                    case 'numeric_notallowzeroMPCheckPackage': return this.validators.notAllowZeroNumberValidatorMPCheckPackage;
                    case 'numeric_notallowzeroMPCheckPackage2': return this.validators.notAllowZeroNumberValidatorMPCheckPackage2;
                    case 'numeric_allownegative': return this.validators.allowNegativeNumberValidator;
                    case 'numeric_allowminone': return this.validators.allowMinOneNumberValidator;
                    case 'text_notallownull': return this.validators.notAllowTextNull;
                    case 'email_allownull': return this.validators.allowEmailNull;
                    case 'phone_allownull': return this.validators.allowPhoneNull;
                    case 'time_validator': return this.validators.timeValidator;
                    default: return null;
                }
            }

            //# add editor to grid cells
            if (item.edit){
                try {
                    const editor = JSON.parse(item.edit);
                    column.cssClass += ' editable-cell';
                    switch (editor.name){
                        case 'NumericEditor': column.editor = a => new NumericEditor(this.renderer, a, editor.decimalPlaces, getValidator(editor.validator), editor.allowNull);
                            break;
                        case 'TextEditor': column.editor = a => new TextEditor(this.renderer, a, getValidator(editor.validator));
                            break;
                        case 'TimeEditor':
                            // console.log(editor, 'create editor');
                            column.editor = a => new TimeEditor(
                                this.renderer,
                                this.translateService,
                                a,
                                new TimeValidationParams(
                                    editor.params.startTime,
                                    editor.params.endTime,
                                    editor.params.timeList,
                                    editor.params.step
                                ),
                                getValidator(editor.validator),
                                editor.allowNull);
                            break;
                        // case 'DataEditor': column.editor = a => AngularEditors.dateTimePicker(a, this.ordersGrid, this.renderer); break;
                        //break;
                        case 'StringSelectEditor':
                            editor.propName = this.toLower ? this.helper.lowerCaseFirstLetter(editor.propName) : editor.propName;
                            column.editor = a => new SelectEditor<any>(this.renderer, a, (item) => {
                                const options: HTMLOptionElement[] = [];
                                editor.data.forEach(dt => {
                                    let option = this.renderer.createElement('option');
                                    option.text = dt;
                                    option.value = dt;
                                    option.selected = item[editor.propName] === dt;
                                    options.push(option);
                                })

                                return options;
                            }, v => v);
                            break;
                        case 'StringSelectEditorComplex':
                            editor.propName = this.toLower ? this.helper.lowerCaseFirstLetter(editor.propName) : editor.propName;
                            let translates: {[key: string]: string};
                            if (editor.translate){
                                this.translateService.get(editor.data.map(d => d.text)).subscribe( t => translates = t);
                            }
                            column.editor = a => new SelectEditor<any>(this.renderer, a, (item) => {
                                const options: HTMLOptionElement[] = [];
                                editor.data.forEach((dt: {val: string, text: string}) => {
                                    let option = this.renderer.createElement('option');
                                    option.text = translates ? translates[dt.text] : dt.text;
                                    option.value = dt.val;
                                    option.selected = item[editor.propName] === dt.val;
                                    options.push(option);
                                })

                                return options;
                            }, v => v);
                            break;
                    }
                } catch (e){
                    console.error(e)
                }
            }

            //# add excel block cell for file sending
            if (item.excelCellBlock){
                column.excelCellBlock = true;
            }

            //# set min width of the column
            if (this.minWidth > 0){
                column.minWidth = this.minWidth;
            }

            if (item.filterType || item.grouping || item.aggregation){
                let cf = +!!item.filterType + +!!item.grouping + +!!item.aggregation;

                const setMinWidth = (minWidth: number) => {
                    column.minWidth = minWidth;
                }

                const getMinWidth = (): number => {
                    return typeof column.minWidth === 'undefined' || typeof column.minWidth === 'object' ? 0 : column.minWidth;
                }

                switch (cf){
                    case 0: break;
                    case 1: getMinWidth() < 57 && setMinWidth(57); break;
                    case 2: getMinWidth() < 67 && setMinWidth(67); break;
                    case 3: getMinWidth() < 91 && setMinWidth(91); break;
                }
            }

            //# add tooltip to the cell
            if (item.tooltip === 'auto' || item.tooltip === 'show'){
                column.tooltip = item.tooltip;
            }

            //# if column visible add it to grid
            if (item.visibility === 1 || item.visibility === 2 && isAdmin){
                columns.push(column);
            }
        });



        //# add custom filters or formatters to grid
        this.addCustomFormatter ? this.addCustomFormatter(columnsSettings, columns) : null;

        this.addSourceFilter ? this.addSourceFilter(columnsSettings, columns) : null;

        this.addTotalsFormatter ? this.addTotalsFormatter(columnsSettings, columns) : null;

        /*if (gridRef){
            gridRef.setColumns(columns);
        } else{
            this.helper.wait(() =>{
                if (gridRef)
                    gridRef.setColumns(columns);
            }, 100)
        }*/

        // console.log(columns, 'columns')

        return columns
    }

}



// WEBPACK FOOTER //
// ./src/app/slick-grid/models/columns-generator.ts